import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { verifyAuthCode, resendActivationCode } from "../../Services/ApiCallsServices/AuthenticationApi";
import { useLocation, useNavigate } from "react-router-dom";
import logo from '../../logos/storage-sprint.png';

const Verification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userEmail = location.state?.email || "";
  const role = location.state?.role || "user";
  const [activationCode, setActivationCode] = useState("");
  const [responseMessage, setResponseMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleActivationCodeChange = (event) => {
    setActivationCode(event.target.value);
  };

  const handleManualActivation = async (e) => {
    e.preventDefault();
    if (!activationCode) {
      setResponseMessage({ type: "error", text: "Please enter an activation code" });
      return;
    }

    setIsLoading(true);
    try {
      const data = await verifyAuthCode(activationCode);
      setResponseMessage({ type: "success", text: data.message || "An error occurred while submitting your request." });
      if (role === "driver") {
        navigate("/review-pending")
      }
      else {
        navigate("/welcome-customer");
      }
    } catch (error) {
      setResponseMessage({ type: "error", text: error.message || "An error occurred while submitting your request." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewActivationLink = async (e) => {
    e.preventDefault();
    if (!userEmail) {
      setResponseMessage({ type: "error", text: "No email address provided." });
      return;
    }

    setIsResending(true);
    try {
      const data = await resendActivationCode(userEmail);
      setResponseMessage({ type: "success", text: data.message || "New activation code sent successfully!" });
    } catch (error) {
      setResponseMessage({ type: "error", text: error.message || "Failed to send new activation code." });
    } finally {
      setIsResending(false);
    }
  };

  const handleEditEmail = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>Verify Your Email | Storage Sprint</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-md overflow-hidden p-6 md:p-8">
          <div className="text-center mb-6">
            <img
                src={logo}
                alt="Storage Sprint Logo"
                className="mx-auto mb-6 w-24 h-auto"
            />
            <h1 className="text-2xl font-bold text-gray-800 mb-2"><span className="text-yellow-500">Welcome to</span> Storage Sprint!</h1>
            <p className="text-gray-600">
              We've sent a verification email to <strong className="text-yellow-600">{userEmail}</strong> with an activation code.
            </p>
          </div>

          {responseMessage && (
            <div className={`mb-4 p-3 rounded-md ${
              responseMessage.type === "success" 
                ? "bg-green-100 text-green-700" 
                : "bg-red-100 text-red-700"
            }`}>
              {responseMessage.text}
            </div>
          )}

          <form onSubmit={handleManualActivation} className="space-y-4">
            <div>
              <input
                type="text"
                placeholder="Enter activation code..."
                value={activationCode}
                onChange={handleActivationCodeChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full px-4 py-2 rounded-md text-white font-medium ${
                  isLoading 
                    ? 'bg-yellow-400 cursor-not-allowed' 
                    : 'bg-yellow-500 hover:bg-yellow-600'
                } transition-colors`}
              >
                {isLoading ? 'Activating...' : 'Activate Account'}
              </button>

              <button
                type="button"
                onClick={handleNewActivationLink}
                disabled={isResending}
                className={`w-full px-4 py-2 rounded-md font-medium border ${
                  isResending 
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
                    : 'border-yellow-500 text-yellow-600 hover:bg-yellow-50'
                } transition-colors`}
              >
                {isResending ? 'Sending...' : 'Request New Code'}
              </button>

              <button
                type="button"
                onClick={handleEditEmail}
                className="w-full px-4 py-2 rounded-md font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Edit Email Address
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Verification;
